import React, {Component, SyntheticEvent} from 'react';
import {FormInput} from '@homeaway/react-form-components';
import {currencyFormatter} from '@homeaway/hacommons-i18n';
import {
    TrackingContextValue,
    TrackingContext,
} from '@homeaway/react-event-tracking';

import {GENERIC_EVENT} from '../../utils/edap';

import './PriceInput.less';

type StateProps = {
    currency: string;
    fullLocale: string;
};

type OwnProps = {
    id: string;
    name: string;
    isActive?: boolean;
    value: number;
    valueIsValid?: boolean;
    minAllowedPrice?: number | null;
    maxAllowedPrice?: number | null;
    label: string;
    onChange: (value: number, name: string) => void;
    onEnterPress: () => void;
    onCheckHasError?: (hasError: boolean) => void;
    hasErrorCallback?: Function;
    errorMsg?: string;
    edapLabel?: string;
    valueIsInfinite?: boolean;
};

type Props = StateProps & OwnProps;

type State = {
    value: string;
    previousPropsValue: number;
    hasError: boolean;
    focused: boolean;
    previousPropsValueIsValid: boolean;
};

export class PriceInput extends Component<Props, State> {
    constructor(props: Props) {
        super(props);

        const {value} = props;

        this.state = {
            previousPropsValue: value,
            value: value.toString(),
            focused: false,
            hasError: false,
            previousPropsValueIsValid:
                typeof props.valueIsValid !== 'undefined'
                    ? props.valueIsValid
                    : true,
        };
    }

    static getDerivedStateFromProps(props: Props, state: State) {
        if (
            state.previousPropsValue !== props.value ||
            (typeof props.valueIsValid !== 'undefined' &&
                state.previousPropsValueIsValid === false &&
                props.valueIsValid)
        ) {
            return {
                value: props.value.toString(),
                previousPropsValue: props.value,
                hasError: false,
                previousPropsValueIsValid: true,
            };
        }

        if (
            typeof props.valueIsValid !== 'undefined' &&
            state.previousPropsValueIsValid &&
            props.valueIsValid === false
        ) {
            return {
                previousPropsValueIsValid: false,
            };
        }

        return null;
    }

    getFormattedWithCurrency = (value: number): string => {
        const {currency, fullLocale} = this.props;
        return currencyFormatter.formatNumber({
            value,
            currencyCode: currency,
            locale: fullLocale,
        });
    };

    handleFocus = () => {
        this.setState({
            focused: true,
            value: '',
            hasError: false,
        });
    };

    validateValue = (inputValue: string): string => {
        const {valueIsInfinite, maxAllowedPrice} = this.props;

        if (
            typeof valueIsInfinite !== 'undefined' &&
            typeof maxAllowedPrice === 'number' &&
            +inputValue >= maxAllowedPrice
        ) {
            return maxAllowedPrice.toString();
        }

        return inputValue;
    };

    hasError = (value: number): boolean => {
        const {minAllowedPrice, maxAllowedPrice, hasErrorCallback} = this.props;

        if (hasErrorCallback) {
            return hasErrorCallback(value, minAllowedPrice, maxAllowedPrice);
        }
        return false;
    };

    handleBlur = (event: SyntheticEvent<HTMLInputElement>) => {
        // start fix Firefox input autoblur issue
        if (
            // @ts-ignore explicitOriginalTarget
            event.nativeEvent.explicitOriginalTarget &&
            // @ts-ignore explicitOriginalTarget
            event.nativeEvent.explicitOriginalTarget ===
                // @ts-ignore explicitOriginalTarget
                event.nativeEvent.originalTarget
        ) {
            return;
        }
        // end fix Firefox input autoblur issue

        const {value: propsValue, name, onChange, onCheckHasError} = this.props;

        const inputValue = event.currentTarget.value;
        const shouldRevertValue = inputValue === '';

        if (!shouldRevertValue) {
            this.triggerEdapEvent(inputValue);
        }

        const value = shouldRevertValue
            ? propsValue.toString()
            : this.validateValue(inputValue);

        const hasError = this.hasError(+value);

        if (onCheckHasError) {
            onCheckHasError(hasError);
        }

        this.setState({
            focused: false,
            hasError,
            value,
        });

        if (!shouldRevertValue && !hasError) {
            onChange(+value, name);
        }

        event.stopPropagation();
    };

    triggerEdapEvent = (value: string) => {
        const {edapLabel} = this.props;
        const edapContext: TrackingContextValue = this.context;

        if (edapLabel) {
            const eventObj = {
                eventaction: `filter selected`,
                eventlabel: `${edapLabel}${value}`,
            };

            edapContext.trigger(GENERIC_EVENT, eventObj);
        }
    };

    handleChange = (event: SyntheticEvent<HTMLInputElement>) => {
        this.setState({
            value: event.currentTarget.value,
        });
    };

    handleKeyPress = (event: KeyboardEvent) => {
        const {onEnterPress} = this.props;
        const code = event.which || event.keyCode;

        if (event.key === 'Enter') {
            (event.currentTarget as HTMLElement).blur();
            if (onEnterPress) {
                onEnterPress();
            }
        }

        // fix firefox number input issue that allow letters
        if (code < 48 || code > 57) {
            event.preventDefault();
        }
    };

    render() {
        const {
            id,
            isActive,
            label,
            name,
            errorMsg,
            valueIsInfinite,
        } = this.props;
        const {hasError} = this.state;

        const priceWithCurrency = this.getFormattedWithCurrency(
            +this.state.value
        );
        const formattedValue = `${priceWithCurrency}${
            valueIsInfinite && !hasError ? '+' : ''
        }`;

        return (
            <div className="PriceInput">
                {this.state.focused && (
                    <FormInput
                        id={id}
                        inputClassName={`PriceInput__field ${
                            isActive ? 'PriceInput__field--is-active' : ''
                        }`}
                        type="number"
                        pattern="\d*"
                        name={name}
                        label={label}
                        value={this.state.value}
                        min="0"
                        onChange={this.handleChange}
                        onBlur={this.handleBlur}
                        onKeyPress={this.handleKeyPress}
                        autoFocus={true} // eslint-disable-line
                        autoComplete="off"
                        errorMsg={hasError && errorMsg}
                    />
                )}

                {!this.state.focused && (
                    <FormInput
                        id={id}
                        inputClassName={`PriceInput__field ${
                            isActive ? 'PriceInput__field--is-active' : ''
                        }`}
                        label={label}
                        name={name}
                        value={formattedValue}
                        onFocus={this.handleFocus}
                        errorMsg={hasError && errorMsg}
                    />
                )}
            </div>
        );
    }
}

PriceInput.contextType = TrackingContext;

export default PriceInput;
